

import { useLocation, useNavigation, useRevalidator, useSearchParams, useRouteLoaderData } from "@remix-run/react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClientOnly } from "~/components/blocks/ClientOnly/client-only";
import { Pagination, PaginationContent, PaginationItem, PaginationPrevious, PaginationNext } from "~/components/ui/custom-radix-components/pagination";
import PGridCard from "~/components/blocks/Card/PGridCard";
import Breadcrumbs from "../BreadCrumbs";
import {
  appendGeosearchFiltersToPropertyPath,
  buildPropertyBreadcrumbSegmentsFromArea,
  formatLocationAddressQueryLabel,
} from "~/components/blocks/Geosearch/locationGeosearch";
import { PropertyData, Workspace } from "./interfaces";
import MobileSearchBar from "./MobileSearchbar";
import { buildingSlug, listingSlug, propertySlug, workspaceSlug } from "~/lib/helpers/slugHelper";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";
import {
  buildCardListingDisplayFromListing,
  resolveListingIsFeatured,
  collectFeaturedPropertyIds,
} from "~/lib/helpers/cardListingDisplay";
import { useProgressiveRenderCount } from "~/lib/hooks/useProgressiveRenderCount";
import {
  buildAreaPageListingHeadline,
  buildAreaPageListingResultText,
  buildAreaPageListingSubheadline,
  getAreaHeadlineBrowseContext,
  resolveAreaPageHeadlineSpaceType,
} from "~/utils/Meta/areaMeta";
import { buildGeoSearchPageListingSubheadline } from "~/utils/Meta/geoSearchMeta";
import { buildLocationGeosearchPageListingSubheadline } from "~/utils/Meta/locationGeosearchMeta";
import { MapPin } from "lucide-react";
import Text from "~/components/atoms/Text";
import { buildSearchPageHref } from "~/lib/helpers/StringHelper";
import AreaMobileLayout from "../AreaPage/MobileLayout";
import { EmptyLazyFallback, loadLazyModule } from "~/lib/helpers/resolveLazyModule";
const CustomMap = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Map/map.client"),
    EmptyLazyFallback,
  ),
);
const NoResults = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Geosearch/NoResult"),
    EmptyLazyFallback,
  ),
);
const FilterDrawer = lazy(() => loadLazyModule(() => import("./MobileDrawer"), EmptyLazyFallback));
const EndOfList = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Geosearch/EndOfList"),
    EmptyLazyFallback,
  ),
);

export default function MobileLayout({
  query,
  workspace,
  filterData,
  callGeoSearch,
  callGeoSearchForDrawer,
  setFilterData,
  data,
  currentPage,
  totalPages,
  handlePageChange,
  renderPaginationItems,
  maxPrice,
  featuredProperties,
  drawerUrl,
  onApplyDrawerFilters,
  parsedQuery,
  areaSummary,
  pageSize,
  hideAreaDesktopLayout = false,
  isProperty = false,
  isLocationGeosearch = false,
}: {
  query: string;
  workspace: Workspace;
  data: any;
  callGeoSearch: Function;
  callGeoSearchForDrawer: Function;
  setFilterData: Function;
  filterData: any;
  currentPage: number;
  totalPages: number;
  handlePageChange: Function;
  renderPaginationItems: Function;
  maxPrice: number;
  featuredProperties: any;
  drawerUrl: string;
  onApplyDrawerFilters: VoidFunction;
  parsedQuery: Record<string, string>;
  areaSummary: any;
  pageSize: number;
  hideAreaDesktopLayout?: boolean;
  /** Property pages render property data/cards without an area profile (no areaSummary). */
  isProperty?: boolean;
  isLocationGeosearch?: boolean;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const [displayMode, setDisplayMode] = useState("grid");
  const [rightWidth, setRightWidth] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(true);
  const [recalibrate, setRecalibrate] = useState(false);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const [hoverIds, setHoverIds] = useState<string[]>([]);

  const handleCardHover = useCallback((listingId?: string | null, propertyId?: string | null) => {
    if (!listingId && !propertyId) {
      setHoverIds([]);
      return;
    }
    const ids = new Set<string>();
    if (listingId) ids.add(String(listingId));
    if (propertyId) ids.add(String(propertyId));
    setHoverIds([...ids]);
  }, []);

  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const isSearchLoading =
    navigation.state === "loading" || revalidator.state === "loading";
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isCrawler = Boolean(
    (useRouteLoaderData("root") as { isCrawler?: boolean } | undefined)?.isCrawler,
  );
  const pageHref = useCallback(
    (page: number) => {
      const path =
        typeof data?.currentLocationPath === "string" && data.currentLocationPath
          ? data.currentLocationPath.split("?")[0]
          : location.pathname;
      const params = new URLSearchParams(searchParams);
      if (
        data?.currentLocationPath &&
        (params.get("location") || "").toLowerCase() === "current"
      ) {
        params.delete("location");
      }
      return buildSearchPageHref(path, params, page);
    },
    [searchParams, location.pathname, data?.currentLocationPath],
  );
  const { country, state, city } = data?.params || {};
  // Datasets - safely access with proper type checking
  const primaryData = (data?.data?.data && Array.isArray(data.data.data)) ? data.data.data : [];
  const hasSearchResults = primaryData.length > 0;
  const mapPinsData = (data?.mapPins && Array.isArray(data.mapPins)) ? data.mapPins : [];
  const nearbyData = (data?.data?.autoReCalibratedData && Array.isArray(data.data.autoReCalibratedData)) ? data.data.autoReCalibratedData : [];
  const nearbyPropertyData =
    data?.nearbyPropertyListings?.data && Array.isArray(data.nearbyPropertyListings.data)
      ? data.nearbyPropertyListings.data
      : [];
  const nearByProperties = (data?.nearByProperties && Array.isArray(data.nearByProperties)) ? data.nearByProperties : [];
  const nearbySectionItems = nearbyPropertyData.length > 0 ? nearbyPropertyData : nearbyData;
  const isViewportDrivenSearch = Boolean(
    parsedQuery?.minLatitude &&
    parsedQuery?.maxLatitude &&
    parsedQuery?.minLongitude &&
    parsedQuery?.maxLongitude,
  );
  // Render property data (property cards + property pins) on area pages
  // (`areaSummary`) and on the SEO property pages (`isProperty`); otherwise the
  // search page shows listing cards/pins.
  const showPropertyData = isProperty || !!areaSummary;
  const mapMarkerData = showPropertyData && mapPinsData.length > 0 ? mapPinsData : primaryData;
  const markerVariant = showPropertyData ? "teal-pin" : "price-pill";

  const listingRenderKey = `${currentPage}-${query}`;
  const visiblePrimaryCount = useProgressiveRenderCount(
    primaryData.length,
    listingRenderKey,
    {
      initial: isCrawler ? primaryData.length : 6,
      batchSize: 6,
      timeoutMs: 200,
    },
  );
  const visibleNearbyCount = useProgressiveRenderCount(
    nearbySectionItems.length,
    listingRenderKey,
    {
      initial: isCrawler ? nearbySectionItems.length : 6,
      batchSize: 6,
      timeoutMs: 200,
    },
  );
  const featuredPropertyIds = useMemo(
    () => collectFeaturedPropertyIds(featuredProperties),
    [featuredProperties],
  );

  const resolveCardIsFeatured = useCallback(
    (record: PropertyData) =>
      resolveListingIsFeatured(record, featuredPropertyIds),
    [featuredPropertyIds],
  );

  // Mapbox GL parse+init is a multi-hundred-ms main-thread task. On mobile the
  // map is off-screen until the user taps Map, so do not boot it during load.
  useEffect(() => {
    if (isCrawler) {
      setShouldLoadMap(false);
      return;
    }
    if (rightWidth > 0) {
      setShouldLoadMap(true);
    }
  }, [isCrawler, rightWidth]);

  const hasPrimary = primaryData.length > 0;
  // Prefer in-city property cards, then the widened "further away" set.
  const hasNearby = nearbySectionItems.length > 0;

  // Smooth scroll to top when pagination changes (mobile UX)
  useEffect(() => {
    // Prefer scrolling the page (works with fixed headers/layout)
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        window.scrollTo(0, 0);
      }
      return;
    }

    // Fallback: scroll the container if we're not in a browser context
    if (parentRef.current) {
      parentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  const toggleDrawer: VoidFunction = () => {
    setIsOpen((prevState) => !prevState);
  };

  // CSS auto-fill so columns are correct on first paint (avoids 1-col → multi-col
  // shift while containerWidth was still 0 awaiting ResizeObserver).
  const cardMinWidth = 330;

  const gridTemplateColumns = useMemo(
    () => ({
      gridTemplateColumns: `repeat(auto-fill, minmax(${cardMinWidth}px, 1fr))`,
    }),
    [],
  );

  // Link helpers
  const withPropertyDetailFilters = useCallback(
    (propertyPath: string) => {
      if (!isProperty || !propertyPath) return propertyPath;
      return appendGeosearchFiltersToPropertyPath(propertyPath, {
        officeTypes: filterData?.officeTypes,
        minPeople: filterData?.minPeople,
        deskRange: filterData?.deskRange,
        officeTypeSlug: data?.officeTypeSlug,
        parsedQuery,
      });
    },
    [
      isProperty,
      filterData?.officeTypes,
      filterData?.minPeople,
      filterData?.deskRange,
      data?.officeTypeSlug,
      parsedQuery,
    ],
  );

  const buildLinks = useCallback((b: any) => {
    const propertyPath = propertySlug({ ...(b as any), id: b.propertyId || b.id } as any);
    const propertyLink = isProperty
      ? withPropertyDetailFilters(propertyPath)
      : propertyPath;
    const link = isProperty ? propertyLink : listingSlug(b as any);
    const buildingLink = b?.buildingData ? buildingSlug(b.buildingData) : "";
    const workspaceLink = b?.workspaceData
      ? workspaceSlug(b.workspaceData as any)
      : "";
    return { link, propertyLink, buildingLink, workspaceLink };
  }, [isProperty, withPropertyDetailFilters]);

  const renderCards = useCallback(
    (items: any[]) =>
      items.map((b: PropertyData, index: number) => {
        const { link, propertyLink, buildingLink, workspaceLink } = buildLinks(b);
        const key = b.id ?? `${b.propertyId ?? "property"}-${index}`;
        const isPropertyCard = showPropertyData;
        return (
          <div key={key} className={index > 0 ? "listing-card-deferred px-1.5" : "px-1.5"}>
            <PGridCard
              {...buildCardListingDisplayFromListing(b, { isProperty: isPropertyCard })}
              loading={isSearchLoading}
              link={link}
              propertyLink={propertyLink}
              isFirstVisible={index === 0}
              buildingLink={buildingLink}
              workspaceLink={workspaceLink}
              altText={b.addressFormula}
              isPropertyPage={isPropertyCard}
              onHover={handleCardHover}
              isFeatured={resolveCardIsFeatured(b)}
              showFavoriteButton={isPropertyCard ? false : true}
              showSpaceTypeChips={true}
            />
          </div>
        );
      }),
    [isSearchLoading, showPropertyData, handleCardHover, resolveCardIsFeatured]
  );

  const renderNearbyCards = useCallback(
    (items: any[]) =>
      items.map((b: PropertyData, index: number) => {
        const nestedProperty =
          b.propertyData && typeof b.propertyData === "object" ? b.propertyData : null;
        const propertyLink = propertySlug({
          ...(nestedProperty || b),
          id: (nestedProperty as any)?.id ?? b.propertyId ?? b.id,
        } as any);
        const buildingLink = b?.buildingData ? buildingSlug(b.buildingData) : "";
        const workspaceLink = b?.workspaceData
          ? workspaceSlug(b.workspaceData as any)
          : "";
        const key = b.id ?? `${b.propertyId ?? "nearby"}-${index}`;
        const isPropertyCard = showPropertyData;
        return (
          <div key={key} className={index > 0 ? "listing-card-deferred px-1.5" : "px-1.5"}>
            <PGridCard
              {...buildCardListingDisplayFromListing(b, { isProperty: isPropertyCard })}
              loading={isSearchLoading}
              link={propertyLink}
              propertyLink={propertyLink}
              isFirstVisible={false}
              buildingLink={buildingLink}
              workspaceLink={workspaceLink}
              altText={b.addressFormula}
              isPropertyPage={isPropertyCard}
              onHover={handleCardHover}
              isFeatured={resolveCardIsFeatured(b)}
              showFavoriteButton={isProperty ? false : true}
              showSpaceTypeChips={true}
            />
          </div>
        );
      }),
    [isSearchLoading, showPropertyData, handleCardHover, resolveCardIsFeatured]
  );

  const renderNearByFeaturedProperties = useCallback((featuredProperties: PropertyData[]) => {
    if (currentPage === 1 && featuredProperties && featuredProperties?.length) {
      return featuredProperties.map((b: PropertyData, index: number) => {
        const propertyLink = withPropertyDetailFilters(propertySlug(b as any));
        const workspaceLink = workspaceSlug(b?.workspaceData as any)
        const buildingLink = buildingSlug(b?.buildingData as any)
        const key = b.id ?? `${b.propertyId ?? "featured"}-${index}`;
        return (
          <div key={`featured-${key}`} className="px-1.5">
            <PGridCard
              {...buildCardListingDisplayFromListing(b, { isProperty: true })}
              isPropertyPage
              isFeatured={true}
              loading={isSearchLoading}
              link={propertyLink}
              propertyLink={propertyLink}
              workspaceLink={workspaceLink}
              buildingLink={buildingLink}
              isFirstVisible={false}
              altText={b.addressFormula}
              onHover={handleCardHover}
              showFavoriteButton={false}
              showSpaceTypeChips={true}
            />
          </div>
        );
      });
    }
    return null;
  }, [isSearchLoading, handleCardHover, currentPage, withPropertyDetailFilters]);

  const areaHeadlineBrowseContext = useMemo(
    () =>
      areaSummary
        ? getAreaHeadlineBrowseContext(searchParams, parsedQuery)
        : {
          firstPageLoad: parsedQuery?.firstPageLoad,
          workspaceType: parsedQuery?.workspaceType,
          hasLocationQueryParams: false,
        },
    [areaSummary, searchParams, parsedQuery],
  );

  const headlineSpaceType = useMemo(
    () =>
      resolveAreaPageHeadlineSpaceType({
        selectedOfficeTypes: filterData?.officeTypes,
        areaSpaceTypeFormula: data?.areaSummary?.spaceTypeFormula,
        areaSpaceType: data?.areaSummary?.spaceType,
        officeTypeFilterTouched: parsedQuery?.officeTypeFilterTouched === "1",
        workspaceType: areaHeadlineBrowseContext.workspaceType,
        firstPageLoad: areaHeadlineBrowseContext.firstPageLoad,
        hasLocationQueryParams: areaHeadlineBrowseContext.hasLocationQueryParams,
      }),
    [
      filterData?.officeTypes,
      data?.areaSummary?.spaceTypeFormula,
      data?.areaSummary?.spaceType,
      parsedQuery?.officeTypeFilterTouched,
      areaHeadlineBrowseContext,
    ],
  );

  const officeTitle = useMemo(() => {
    const summary = data?.areaSummary;
    const paramCity = parsedQuery?.city ? String(parsedQuery.city) : "";
    const paramState = parsedQuery?.state ? String(parsedQuery.state) : "";
    const paramCountry = parsedQuery?.country
      ? String(parsedQuery.country).toLowerCase()
      : "";
    const hasAnyLocationParams = Boolean(
      paramCountry || paramState || paramCity,
    );
    const addressQuery = formatLocationAddressQueryLabel(
      searchParams.get("q") || parsedQuery?.q,
    );

    return buildAreaPageListingHeadline({
      spaceTypeFormula: headlineSpaceType,
      areaSummary: summary,
      paramCity,
      paramState,
      paramCountry,
      areaCity: summary?.city || summary?.suburb || city,
      areaState: summary?.state || state,
      areaCountry: summary?.country || country,
      routeCountry: country,
      hasAnyLocationParams,
      placeOverride: isLocationGeosearch
        ? data?.locationText?.trim() || undefined
        : undefined,
      addressQuery: addressQuery || undefined,
    });
  }, [
    data?.areaSummary,
    data?.locationText,
    country,
    state,
    city,
    parsedQuery?.country,
    parsedQuery?.state,
    parsedQuery?.city,
    parsedQuery?.q,
    headlineSpaceType,
    isLocationGeosearch,
    searchParams,
  ]);

  const officeSubheadline = useMemo(() => {
    const summary = data?.areaSummary ?? areaSummary;
    const listings =
      data?.data?.data && Array.isArray(data.data.data) ? data.data.data : [];
    const fallbackListings = nearbyPropertyData;
    const total =
      data?.data?.totalProperties ?? data?.data?.totalListings ?? 0;
    const paramCity = parsedQuery?.city ? String(parsedQuery.city) : "";
    const paramState = parsedQuery?.state ? String(parsedQuery.state) : "";
    const paramCountry = parsedQuery?.country
      ? String(parsedQuery.country).toLowerCase()
      : "";
    const hasAnyLocationParams = Boolean(
      paramCountry || paramState || paramCity || city || state || country,
    );
    const regionCurrency =
      data?.regionData?.currencyIsoCode ?? data?.regionData?.currency;

    if (isLocationGeosearch || !summary) {
      if (isLocationGeosearch) {
        return buildLocationGeosearchPageListingSubheadline({
          data,
          parsedQuery,
          filterData,
        });
      }
      return buildGeoSearchPageListingSubheadline({
        data,
        parsedQuery,
        filterData,
      });
    }

    return buildAreaPageListingSubheadline({
      areaSummary: summary,
      totalProperties: total,
      listings,
      fallbackListings,
      regionCurrency,
      paramCity,
      paramState,
      paramCountry,
      areaCity: summary?.city || summary?.suburb || city,
      areaState: summary?.state || state,
      areaCountry: summary?.country || country,
      routeCountry: country,
      hasAnyLocationParams,
      selectedOfficeTypes: filterData?.officeTypes,
      areaSpaceTypeFormula: data?.areaSummary?.spaceTypeFormula,
      areaSpaceType: data?.areaSummary?.spaceType,
      officeTypeFilterTouched: parsedQuery?.officeTypeFilterTouched === "1",
      workspaceType: areaHeadlineBrowseContext.workspaceType,
      firstPageLoad: areaHeadlineBrowseContext.firstPageLoad,
      hasLocationQueryParams: areaHeadlineBrowseContext.hasLocationQueryParams,
      globalPriceTier: data?.globalPriceTier,
      mapPins: data?.mapPins,
    });
  }, [
    areaSummary,
    data,
    filterData,
    data?.areaSummary,
    data?.areaSummary?.spaceTypeFormula,
    data?.areaSummary?.spaceType,
    data?.data?.data,
    nearbyPropertyData,
    data?.data?.totalListings,
    data?.data?.totalProperties,
    data?.regionData?.currencyIsoCode,
    data?.regionData?.currency,
    data?.globalPriceTier,
    data?.mapPins,
    parsedQuery?.city,
    parsedQuery?.state,
    parsedQuery?.country,
    parsedQuery?.officeTypeFilterTouched,
    areaHeadlineBrowseContext,
    city,
    state,
    country,
    isLocationGeosearch,
  ]);

  const resultText = useMemo(() => {
    const listings =
      data?.data?.data && Array.isArray(data.data.data) ? data.data.data : [];
    const total = data?.data?.totalProperties ?? data?.data?.totalListings ?? 0;

    if (!listings.length) return null;

    const start = (currentPage - 1) * pageSize + 1;
    const end = start + listings.length - 1;

    if (!areaSummary) {
      return `Showing ${start} - ${end} of ${total.toLocaleString()} spaces`;
    }

    const summary = data?.areaSummary ?? areaSummary;
    const paramCity = parsedQuery?.city ? String(parsedQuery.city) : "";
    const paramState = parsedQuery?.state ? String(parsedQuery.state) : "";
    const paramCountry = parsedQuery?.country
      ? String(parsedQuery.country).toLowerCase()
      : "";
    const hasAnyLocationParams = Boolean(
      paramCountry || paramState || paramCity,
    );

    return buildAreaPageListingResultText({
      start,
      end,
      total,
      spaceTypeFormula: headlineSpaceType,
      areaSummary: summary,
      paramCity,
      paramState,
      paramCountry,
      areaCity: summary?.city || summary?.suburb || city,
      areaState: summary?.state || state,
      areaCountry: summary?.country || country,
      routeCountry: country,
      hasAnyLocationParams,
    });
  }, [
    currentPage,
    pageSize,
    areaSummary,
    headlineSpaceType,
    data?.areaSummary,
    data.data.data,
    data.data.totalListings,
    data.data.totalProperties,
    parsedQuery?.city,
    parsedQuery?.state,
    parsedQuery?.country,
    city,
    state,
    country,
  ]);

  const nearbySectionCity = useMemo(() => {
    if (parsedQuery?.location) {
      const location = decodeURIComponent(String(parsedQuery.location)).trim();
      const cityPart = location.split(",")[0]?.trim();
      if (cityPart) return cityPart;
    }
    const citySlug = parsedQuery?.city ? String(parsedQuery.city) : city ? String(city) : "";
    if (citySlug) {
      const decoded = decodeURIComponent(citySlug);
      return decoded.charAt(0).toUpperCase() + decoded.slice(1).replace(/-/g, " ");
    }
    if (areaSummary?.city || areaSummary?.suburb) {
      return areaSummary.city || areaSummary.suburb;
    }
    const fromLocationText = data?.locationText?.split(",")[0]?.trim();
    return fromLocationText || "your area";
  }, [
    parsedQuery?.location,
    parsedQuery?.city,
    city,
    areaSummary?.city,
    areaSummary?.suburb,
    data?.locationText,
  ]);

  return (
    <div>
      <MobileSearchBar
        toggleDrawer={toggleDrawer}
        setWidth={setRightWidth}
        rightWidth={rightWidth}
        setDisplayMode={setDisplayMode}
        filterData={filterData}
        callGeoSearch={callGeoSearch}
        setFilterData={setFilterData}
        query={query}
        workspace={workspace}
        displayMode={displayMode}
        setMapLoaded={setMapLoaded}
        areaSummary={areaSummary}
        setRecalibrate={setRecalibrate}
      />


      {isOpen ? (
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <FilterDrawer areaSummary={areaSummary} maxPrice={maxPrice} open={isOpen} setOpen={setIsOpen} data={data} filterData={filterData} callGeoSearch={callGeoSearchForDrawer} workspace={data?.workspace} url={drawerUrl} onApply={onApplyDrawerFilters} isPropertyPage={isProperty} />
        </Suspense>
      ) : null}

      <div className="w-full pt-28 min-h-[calc(100vh-136px)]" ref={parentRef}>
        {/* Left Section */}
        {!(mapLoaded && rightWidth > 0) && (
          <>
            <div className="px-4 py-2">
              <div className="flex flex-col min-w-0 overflow-hidden gap-1.5">
                {hasSearchResults && <h1 className="text-xl font-semibold">{officeTitle}</h1>}
                {officeSubheadline && hasSearchResults ? (
                  <span className="text-sm font-normal text-oh-dark-gray inline-flex items-center gap-0.5">
                    {officeSubheadline}
                  </span>
                ) : null}
                <div className="flex items-end pt-4 justify-between gap-4 min-w-0">
                  {resultText ? (
                    <Text className="text-sm font-medium text-oh-dark-gray">
                      {resultText}
                    </Text>
                  ) : null}
                </div>
              </div>
            </div>
            {/* Featured Properties Section - Reserve space to prevent CLS */}
            {data?.data?.data && Array.isArray(data.data.data) && data.data.data.length > 0 && (
              <div className="grid gap-2 mb-1 px-4" style={gridTemplateColumns}>
                {renderNearByFeaturedProperties(featuredProperties || [])}
              </div>
            )}
            <div
              id={PAGE_SECTION_IDS.searchResults}
              style={{ position: rightWidth === 100 ? "absolute" : "initial" }}
              className={`transition-all duration-300 px-1.5 min-h-[calc(100vh-136px)] z-0 w-full ${PAGE_SECTION_SCROLL_CLASS}`}
            >

              {hasPrimary ? (
                <div className="grid gap-2" style={gridTemplateColumns}>
                  {renderCards(primaryData.slice(0, visiblePrimaryCount))}
                </div>
              ) : (
                <Suspense fallback={<div className="min-h-[300px] px-1.5" />}>
                  <div className="px-1.5">
                    <NoResults
                      query={data?.query}
                      params={data?.params}
                      parsedQuery={parsedQuery}
                      callGeoSearch={callGeoSearch as any}
                      areaSummary={areaSummary}
                      isMobile
                      isPropertyPage={isProperty}
                      locationBasePath={data?.locationBasePath}
                      locationLevel={data?.level}
                    />
                  </div>
                </Suspense>
              )}

              {/* End of list only for primary */}
              {hasPrimary && currentPage === totalPages ? (
                <Suspense fallback={null}>
                  <EndOfList
                    query={data?.query}
                    params={data?.params}
                    parsedQuery={parsedQuery}
                    callGeoSearch={callGeoSearch as any}
                    areaSummary={areaSummary}
                    isPropertyPage={isProperty}
                    locationBasePath={data?.locationBasePath}
                    locationLevel={data?.level}
                  />
                </Suspense>
              ) : null}

              {/* Nearby section: show whenever nearby exists (with heading), regardless of primary */}
              {hasNearby ? (
                <div className="mt-6">
                  <h2 className="font-semibold mb-2 text-lg px-1.5">
                    {nearbyPropertyData.length > 0
                      ? `More options in ${nearbySectionCity}`
                      : "More picks from further away"}
                  </h2>
                  <div className="grid gap-2" style={gridTemplateColumns}>
                    {renderNearbyCards(nearbySectionItems.slice(0, visibleNearbyCount))}
                  </div>
                </div>
              ) : (
                // Reserve space for nearby section to prevent CLS if it loads later
                <div className="min-h-[10px] mt-6" />
              )}
            </div>
          </>
        )}

        {/* Right Section (Map) — only after the user opens map view */}
        {shouldLoadMap ? (
          <div
            id={PAGE_SECTION_IDS.map}
            ref={rightContainerRef}
            className={`transition-all duration-300 fixed top-[112px] right-0 h-[calc(100dvh-112px)] z-[39] ${PAGE_SECTION_SCROLL_CLASS}`}
            style={{ width: `${rightWidth}%` }}
          >
            <Suspense fallback={
              <div className="h-full w-full flex items-center justify-center bg-gray-50">
                <p className="text-lg text-gray-500">Loading map...</p>
              </div>
            }>
              <ClientOnly fallback={
                <div className="h-full w-full flex items-center justify-center bg-gray-50">
                  <p className="text-lg text-gray-500">Loading map...</p>
                </div>
              }>
                {() => (
                  <CustomMap
                    data={mapMarkerData}
                    totalListings={data?.data?.totalProperties ?? data?.data?.totalListings ?? 0}
                    rightWidth={rightWidth}
                    rightContainerRef={rightContainerRef}
                    width={rightWidth}
                    setWidth={setRightWidth}
                    loading={isSearchLoading}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    callGeoSearch={callGeoSearch}
                    displayMode={displayMode}
                    nearbyListings={nearbyData}
                    nearByProperties={isViewportDrivenSearch ? nearByProperties : []}
                    mobile={true}
                    query={query}
                    region={country}
                    recalibrate={recalibrate}
                    setRecalibrate={setRecalibrate}
                    showPanelControl={false}
                    parsedQuery={parsedQuery}
                    locationPolygon={data?.locationPolygon}
                    locationPolygonBounds={data?.locationPolygonBounds}
                    globalPriceTier={data?.globalPriceTier ?? null}
                    showPerDesk={showPropertyData}
                    isPropertyCard={showPropertyData}
                    featuredPropertyIds={featuredPropertyIds}
                    markerVariant={markerVariant}
                    hoverIds={hoverIds}
                    onHover={handleCardHover}
                    isAreaPage={!!areaSummary}
                    pageSize={pageSize}
                  />
                )}
              </ClientOnly>
            </Suspense>
          </div >
        ) : rightWidth > 0 ? (
          <div
            className="transition-all duration-300 fixed top-[112px] right-0 h-[calc(100dvh-112px)] z-[39] bg-gray-50 flex items-center justify-center"
            style={{ width: `${rightWidth}%` }}
          >
            <p className="text-lg text-gray-500">Loading map...</p>
          </div>
        ) : null
        }
      </div >

      {/* Pagination - only for primary dataset */}
      {
        !(mapLoaded && rightWidth > 0) && (
          <>
            <div className="flex flex-col items-start pb-4 px-4">
              {/* Reserve space for pagination to prevent CLS */}
              <div className="flex justify-center items-center my-4 w-full min-h-[48px]">
                {hasPrimary && totalPages > 1 && primaryData.length > 0 ? (
                  <Pagination className="justify-center">
                    <PaginationContent>
                      <PaginationItem className="cursor-pointer">
                        <PaginationPrevious
                          to={currentPage > 1 ? pageHref(currentPage - 1) : ""}
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                      </PaginationItem>

                      {renderPaginationItems()}

                      <PaginationItem className="cursor-pointer">
                        <PaginationNext
                          to={currentPage < totalPages ? pageHref(currentPage + 1) : ""}
                          disabled={currentPage >= totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                ) : null}
              </div>

            </div>



            {areaSummary ? (
              <div className="bg-gray-100 px-4 py-2">
                <Breadcrumbs
                  params={data?.params}
                  countryRequired={true}
                  isPropertyPage={true}
                  segments={buildPropertyBreadcrumbSegmentsFromArea(areaSummary)}
                  useCurrentPathForLastSegment
                />
              </div>
            ) : (
              <div className="bg-gray-100 px-4 py-2">
                <Breadcrumbs params={data?.params} countryRequired={isProperty} isPropertyPage={isProperty} />
              </div>
            )}
          </>
        )
      }
      {
        areaSummary && !hideAreaDesktopLayout && (
          <div className="area-seo-below-fold">
            <AreaMobileLayout
              data={data}
              areaSummary={areaSummary}
              faqs={data.faqs}
              nearbyOffices={data.nearbyOffices}
              areaBlogs={data.areaBlogs}
              region={data.region}
            />
          </div>
        )
      }
    </div >
  );
}
